import { useState, useCallback, useRef, useEffect } from 'react';
import { Pattern, Stitch } from '../types/pattern';
import { savePattern } from '../utils/storage';
import { MAX_HISTORY } from '../utils/constants';

export function usePatternHistory(startPattern: Pattern) {
  const [pattern, setPattern] = useState<Pattern>(startPattern);
  // Store only stitches arrays in history — undo/redo only affects stitches
  const [historyState, setHistoryState] = useState<{ entries: Stitch[][]; index: number }>({
    entries: [startPattern.stitches],
    index: 0,
  });

  // Debounced pattern save
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const pendingSaveRef = useRef<Pattern | null>(null);

  const debouncedSave = useCallback((p: Pattern) => {
    pendingSaveRef.current = p;
    clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      savePattern(p);
      pendingSaveRef.current = null;
    }, 500);
  }, []);

  // Flush pending saves on page unload
  useEffect(() => {
    const flushSave = () => {
      if (pendingSaveRef.current) {
        savePattern(pendingSaveRef.current);
        pendingSaveRef.current = null;
      }
    };
    window.addEventListener('beforeunload', flushSave);
    return () => window.removeEventListener('beforeunload', flushSave);
  }, []);

  // Pattern ref for callbacks that need latest pattern without re-creating
  const patternRef = useRef(pattern);
  patternRef.current = pattern;

  const pushToHistory = useCallback((newPattern: Pattern) => {
    setHistoryState(prev => {
      const newEntries = prev.entries.slice(0, prev.index + 1);
      newEntries.push(newPattern.stitches);
      if (newEntries.length > MAX_HISTORY) newEntries.shift();
      return { entries: newEntries, index: newEntries.length - 1 };
    });
  }, []);

  const updatePattern = useCallback((newPattern: Pattern) => {
    setPattern(newPattern);
    pushToHistory(newPattern);
    debouncedSave(newPattern);
  }, [pushToHistory, debouncedSave]);

  const handleUndo = useCallback(() => {
    setHistoryState(prev => {
      if (prev.index > 0) {
        const newIndex = prev.index - 1;
        setPattern(current => ({ ...current, stitches: prev.entries[newIndex] }));
        return { ...prev, index: newIndex };
      }
      return prev;
    });
  }, []);

  const handleRedo = useCallback(() => {
    setHistoryState(prev => {
      if (prev.index < prev.entries.length - 1) {
        const newIndex = prev.index + 1;
        setPattern(current => ({ ...current, stitches: prev.entries[newIndex] }));
        return { ...prev, index: newIndex };
      }
      return prev;
    });
  }, []);

  const resetHistory = useCallback((newPattern: Pattern) => {
    setPattern(newPattern);
    setHistoryState({ entries: [newPattern.stitches], index: 0 });
  }, []);

  return {
    pattern,
    setPattern,
    patternRef,
    historyState,
    updatePattern,
    pushToHistory,
    debouncedSave,
    handleUndo,
    handleRedo,
    resetHistory,
  };
}
